import {
    ANALYTICS_EVENT_TYPES,
    logEvent as logAnalyticsEvent,
} from '../analytics';
import { logEvent as logInsightsEvent } from '../logging';
import {
    craftMAFallbackMessage,
    craftMARedirectFollowupMessage,
} from '../messageassist';
import { GlobalInformationAlertType, ScheduledRedirectValue } from '../types';
import {
    MESSAGE_ASSIST_SCHEMA,
    MESSAGE_ASSIST_MESSAGE_EVENT_TYPES,
} from '../constants';
import { GraceFeatureFlags } from '../featureFlags';
import { FaqData } from '../attachments';
import { InputMode } from '../hooks';
import { AmpFlagSet } from '../amplitude';
import general from '../resources/general.json';
import { setFulfillmentLinkShownForGreetingOptimization } from '../ReactChat';

export interface PresentMASendMessageDeps {
    featureFlags: AmpFlagSet;
    setInputMode: (inputMode: InputMode) => void;
    messages: any[];
    setMessages: (messages: any[]) => void;
    showGlobalInformationAlert: (
        type: GlobalInformationAlertType,
        value: ScheduledRedirectValue
    ) => void;
}

const triggerSendMessageAutoRedirect = (
    utterance: string,
    intent: string,
    source: string,
    deps: PresentMASendMessageDeps,
    faqData?: FaqData
) => {
    const {
        featureFlags,
        setInputMode,
        messages,
        setMessages,
        showGlobalInformationAlert,
    } = deps;

    let target = `${MESSAGE_ASSIST_SCHEMA}${MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.SEND_MESSAGE}`;
    const categoricalExperienceEnabled =
        !!featureFlags[GraceFeatureFlags.MessageAssistCategoricalExperience];
    target += `?showCategories=${categoricalExperienceEnabled}`;
    showGlobalInformationAlert(
        GlobalInformationAlertType.ScheduledRedirectNotice,
        {
            target,
            delayMs: 0,
            amplitudeEvent: {
                eventType: ANALYTICS_EVENT_TYPES.CATEGORICAL_AUTO_REDIRECT,
            },
            redirectCallback: () => {
                setFulfillmentLinkShownForGreetingOptimization(intent);
                setTimeout(() => {
                    setInputMode('disabled');
                    setMessages([
                        ...messages,
                        craftMARedirectFollowupMessage(
                            utterance,
                            intent,
                            source,
                            faqData,
                            categoricalExperienceEnabled
                        ),
                    ]);
                }, 1000); // Ensure redirect can happen before message is posted to prevent visual noise
            },
        } as ScheduledRedirectValue
    );
};

export const presentMASendMessage = (
    utterance: string,
    intent: string,
    source: string,
    deps: PresentMASendMessageDeps,
    faqData?: FaqData
) => {
    const { setInputMode, messages, setMessages } = deps;

    logAnalyticsEvent(ANALYTICS_EVENT_TYPES.USER_TYPED_SEND_MESSAGE);
    logInsightsEvent({
        name: 'Present default send message response to medical question from user input',
    });

    if (faqData?.autoRedirect) {
        triggerSendMessageAutoRedirect(
            utterance,
            intent,
            source,
            deps,
            faqData
        );
    } else {
        setInputMode('disabled');
        setMessages([
            ...messages,
            craftMAFallbackMessage(
                utterance,
                intent,
                source,
                faqData,
                general.providence.sendMessageForMedicalQuestion
            ),
        ]);
    }
};
